import cx from 'classnames';
import { Formik } from 'formik';
import { Form } from 'react-bootstrap';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { selectBookADemoFormOpen, setBookADemoFormOpen } from '../../redux/slicers/appSlicer';
import InfoDialogUI from '../../components/InfoDialog/InfoDialogUI';
import { emailRegExp, getUniqueId, getUserTimezone, phoneRegexV2 } from '../../helpers/jsHelper';
import PhoneInput from 'react-phone-input-2';
import BigButton from '../BigButton';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import '../../screens/Login.scss';
import Loader from '../Loader';
import { useInfoDialogaActions } from '../../hooks/useInfoDialogaActions';
import { getCookie } from '../../helpers/apiHelper';
import { errorToast } from '../../helpers/toastHelper';
import { LogConsole } from '../../helpers/logEvents';
import { GOOGLE_REAPTCHA_V2_KEY } from '../../constants/constants';
import ReCAPTCHA from 'react-google-recaptcha';

const COUNTRY_LIST = [
  'Australia',
  'Canada',
  'India',
  'United Kingdom',
  'United States',
  'Italy',
  'Brazil',
  'Denmark',
  'Norway',
  'Germany',
  'France',
  'Spain',
  'Finland',
  'Sweden',
  'New Zealand',
  'Ireland',
  'Netherlands',
  'Other',
];

const COUNTRY_CODE_MAP = {
  Australia: 'au',
  Canada: 'ca',
  India: 'in',
  'United Kingdom': 'gb',
  'United States': 'us',
  Italy: 'it',
  Brazil: 'br',
  Denmark: 'dk',
  Norway: 'no',
  Germany: 'de',
  France: 'fr',
  Spain: 'es',
  Finland: 'fi',
  Sweden: 'se',
  'New Zealand': 'nz',
  Ireland: 'ie',
  Netherlands: 'nl',
};

const MEETING_LINK_SLUG = 'imagine-io/book-a-meeting'; // 'gagandeep2/book-a-meeting';
const INDIA_MEETING_LINK_SLUG = 'imagine-io/book-a-meeting-india'; // 'nikhil-saini/india';

const MEETING_AVAILIBITY_API =
  'https://esox4mb3x7swrvx3zh5jlele3e0vkbgf.lambda-url.us-east-1.on.aws/';

const BOOK_MEETING_API = 'https://sjgwafku6wivgmenb52ipcdmi40kmaes.lambda-url.us-east-1.on.aws/';

const TIMEZONE_COUNTRY_MAP = {
  'Australia/Sydney': 'Australia',
  'Australia/Melbourne': 'Australia',
  'Australia/Brisbane': 'Australia',
  'Australia/Perth': 'Australia',
  'Australia/Adelaide': 'Australia',
  'Australia/Darwin': 'Australia',
  'Australia/Hobart': 'Australia',

  'America/Toronto': 'Canada',
  'America/Vancouver': 'Canada',
  'America/Montreal': 'Canada',
  'America/Edmonton': 'Canada',
  'America/Winnipeg': 'Canada',
  'America/Halifax': 'Canada',
  'America/St_Johns': 'Canada',

  'Asia/Kolkata': 'India',
  'Asia/Calcutta': 'India', // Official IANA timezone
  'Asia/New Delhi': 'India', // Alternative used in some systems

  'Europe/London': 'United Kingdom',
  'Europe/Belfast': 'United Kingdom',
  'Europe/Jersey': 'United Kingdom',
  'Europe/Guernsey': 'United Kingdom',
  'Europe/Isle_of_Man': 'United Kingdom',

  'America/New_York': 'United States',
  'America/Chicago': 'United States',
  'America/Denver': 'United States',
  'America/Los_Angeles': 'United States',
  'America/Phoenix': 'United States',
  'America/Detroit': 'United States',
  'America/Indianapolis': 'United States',
  'America/Anchorage': 'United States',
  'Pacific/Honolulu': 'United States',

  'Europe/Rome': 'Italy',
  'Europe/Vatican': 'Italy',
  'Europe/San_Marino': 'Italy',

  'America/Sao_Paulo': 'Brazil',
  'America/Rio_Branco': 'Brazil',
  'America/Fortaleza': 'Brazil',
  'America/Belem': 'Brazil',
  'America/Manaus': 'Brazil',
  'America/Recife': 'Brazil',
  'America/Cuiaba': 'Brazil',

  'Europe/Copenhagen': 'Denmark',
  'America/Danmarkshavn': 'Denmark',
  'Atlantic/Faroe': 'Denmark',
  'America/Scoresbysund': 'Denmark',

  'Europe/Oslo': 'Norway',
  'Arctic/Longyearbyen': 'Norway',

  'Europe/Berlin': 'Germany',
  'Europe/Frankfurt': 'Germany',
  'Europe/Busingen': 'Germany',

  'Europe/Paris': 'France',
  'Europe/Monaco': 'France',

  'Europe/Madrid': 'Spain',
  'Atlantic/Canary': 'Spain',
  'Europe/Ceuta': 'Spain',

  'Europe/Helsinki': 'Finland',
  'Europe/Mariehamn': 'Finland',

  'Europe/Stockholm': 'Sweden',

  'Pacific/Auckland': 'New Zealand',
  'Pacific/Chatham': 'New Zealand',

  'Europe/Dublin': 'Ireland',

  'Europe/Amsterdam': 'Netherlands',
};

const MONTH_MAPPING = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

const getTodayDateWithTimezone = (selectedTimezone = null) => {
  if (!selectedTimezone) return '';
  const date = new Date(); // Convert seconds to milliseconds
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: selectedTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const getDateFromTimestamp = (timestamp = null) => {
  if (!timestamp) return null;

  const date = new Date(timestamp);

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-based
  const day = date.getDate().toString().padStart(2, '0');

  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

const getDateFromTimestampBasedOnTimezone = (timestamp = null, selectedTimezone = null) => {
  if (!timestamp || !selectedTimezone) return null;

  const date = new Date(timestamp); // Convert seconds to milliseconds
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: selectedTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const removeDatepickerStyle = () =>
  document.querySelectorAll(`[id^="date-picker-style"]`).forEach((link) => link.remove());

const addDatePickerStyle = () => {
  const link = document.createElement('link');

  link.id = 'date-picker-style';
  link.rel = 'stylesheet';
  link.href = 'https://cdn.jsdelivr.net/npm/vanillajs-datepicker@1.3.4/dist/css/datepicker.min.css';
  document.head.appendChild(link);
};

const removeDatepickerScript = () =>
  document.querySelectorAll('.date-picker-script').forEach((script) => script.remove());

const addDatePickerScript = ({ intializeCalendar }) => {
  addDatePickerStyle();

  const script = document.createElement('script');

  script.id = 'date-picker-script';
  script.className = 'date-picker-script';
  script.type = 'text/javascript';
  script.src = 'https://cdn.jsdelivr.net/npm/vanillajs-datepicker@1.3.4/dist/js/datepicker.min.js';
  document.body.appendChild(script);

  script.addEventListener('load', () => {
    const userTimezone = getUserTimezone();
    intializeCalendar?.(userTimezone);
  });
};

const createMeeting = async ({
  firstname = '',
  lastname = '',
  email,
  startTime,
  timezone = 'Asia/Calcutta', // india timezone
  callback = null,
  failedCallback = null,
  isIndia = null,
  setFormInfo,
}) => {
  if (!email || !startTime) return console.error('Missing email or startTime');

  try {
    setFormInfo((prev) => ({ ...prev, formSubmitting: true }));
    const response = await fetch(BOOK_MEETING_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        duration: '1800000',
        firstName: firstname,
        lastName: lastname,
        email,
        startTime,
        slug: isIndia ? INDIA_MEETING_LINK_SLUG : MEETING_LINK_SLUG,
        timezone,
      }),
    });

    if (!response.ok) {
      const result = await response.json();
      const message =
        result?.message && typeof result?.message === 'string'
          ? result?.message
          : 'Unable to schedule the meeting. Please try again later!';
      errorToast(message);
      window?.getTimeOptions?.();
      failedCallback?.();
      setFormInfo((prev) => ({ ...prev, formSubmitting: false }));
      return;
    }
    callback?.();
    setFormInfo((prev) => ({ ...prev, formSubmitting: false }));
    return;
  } catch (error) {
    console.error(error);
    failedCallback?.();
  }
};

const sendDataToHubspot = async ({
  values,
  portalId,
  formId,
  callback = null,
  setFormInfo,
  failedCallback = null,
  isIndia = null,
}) => {
  if (!values || !portalId || !formId) return console.error('Missing values, portalId / formId');

  try {
    const { startTime, timezone, ...rest } = values;
    const data = {
      fields: Object.entries(rest)
        .map(([name, value]) => ({ name, value }))
        .filter((f) => f.value),
      context: {
        hutk: getCookie(),
        pageUri: window.location.href,
        pageName: document.title,
      },
    };

    setFormInfo((prev) => ({ ...prev, formSubmitting: true }));
    const response = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const result = await response.json();
      const message =
        result?.message && typeof result?.message === 'string'
          ? result?.message
          : 'Unable to submit the form. Please try again later!';

      errorToast(message);
      failedCallback?.();
      setFormInfo((prev) => ({ ...prev, formSubmitting: false }));
      return;
    }
    LogConsole('HubSpot form submitted!', await response.json());

    if (startTime) {
      await createMeeting({
        ...rest,
        startTime,
        timezone,
        callback,
        failedCallback,
        setFormInfo,
        isIndia,
      });
    } else {
      setFormInfo((prev) => ({ ...prev, formSubmitting: false }));
    }
  } catch (error) {
    console.error(error);
    failedCallback?.();
    setFormInfo((prev) => ({ ...prev, formSubmitting: false }));
  }
};

export const BookADemoForm = () => {
  const dispatch = useDispatch();
  const open = useSelector(selectBookADemoFormOpen);

  if (!open) return null;

  return (
    <BookADemoFormModal
      title="Book a free demo with our experts"
      onClose={() => dispatch(setBookADemoFormOpen(false))}
      portalId="6917454"
      formId="8b24a75f-0f75-43e6-aba1-530ccea06760"
    />
  );
};

const BookADemoFormModal = ({ title, onClose, portalId, formId }) => {
  const initialFormInfo = {
    uniqueId: null,
    selectedDate: '',
    timeOptions: [],
    availableTimeOptions: [],
    selectedTime: null,
    selectedTimezone: '',
    timezoneOptions: [],
    selectedCountry: '',
    formSubmitting: false,
  };

  const [
    {
      uniqueId,
      selectedDate,
      timeOptions,
      availableTimeOptions,
      selectedTime,
      selectedTimezone,
      timezoneOptions,
      selectedCountry,
      formSubmitting,
    },
    setFormInfo,
  ] = useState(initialFormInfo);
  const [APIPayload, setAPIPayload] = useState({
    hasMore: true,
    monthOffset: 0,
  });
  const [contactNumberMessage, setContactNumberMessage] = useState('');
  const [fetchingAvailableTime, setFetchingAvailableTime] = useState(false);
  const { openInfoDialog, closeInfoDialog } = useInfoDialogaActions();
  const prevCountryRef = useRef(null);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(null);

  useLayoutEffect(() => {
    setFormInfo((prev) => ({ ...prev, uniqueId: getUniqueId() }));

    return () => {
      setFormInfo((prev) => ({ ...prev, uniqueId: null }));
      window.datepicker = null;
      removeDatepickerStyle();
      removeDatepickerScript();
    };
  }, []);

  useEffect(() => {
    if (!uniqueId) return;
    addDatePickerScript({ intializeCalendar });

    const userTimezone = getUserTimezone();
    setFormInfo((prev) => ({
      ...prev,
      selectedDate: getTodayDateWithTimezone(userTimezone),
      selectedTimezone: userTimezone,
      selectedCountry: TIMEZONE_COUNTRY_MAP?.[userTimezone] || 'Other',
    }));
  }, [uniqueId]);

  // form initialization
  const initialTouch = {
    firstname: true,
    lastname: true,
    email: true,
    company: true,
    country_01: true,
    custom_country: true,
    startTime: true,
    timezone: true,
  };
  const initialValues = {
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    company: '',
    country_01: '',
    custom_country: '',
    startTime: '',
    timezone: '',
  };

  const validationSchema = Yup.object().shape({
    firstname: Yup.string().required('First name is required'),
    lastname: Yup.string().required('Last Name is required'),
    email: Yup.string()
      .email('Invalid email')
      .matches(emailRegExp, 'Use a business email only')
      .required('Email is required'),
    company: Yup.string().required('Company is required'),
    custom_country: Yup.string().when('country_01', {
      is: 'Other',
      then: Yup.string().required('Country name is required'),
      otherwise: Yup.string().nullable(),
    }),
    phone: Yup.string().matches(phoneRegexV2),
    startTime: Yup.string().required('Please select the meeting time'),
  });

  const afterSuccessCallback = ({ email } = {}) => {
    onClose?.();
    openInfoDialog({
      title: 'Your demo is booked!',
      description: `You'll be receiving an email confirmation to ${email} shortly.`,
      buttonText: 'Okay',
      okBtnColor: 'warning',
      onButtonClick: () => setFormInfo(initialFormInfo),
    });

    // auto-closing the success message after 5 sec
    setTimeout(() => {
      setFormInfo(initialFormInfo);
      closeInfoDialog();
    }, 5000);
  };

  const afterFailedCallback = () => {};

  const onSubmit = (_values, { setSubmitting }) => {
    const { custom_country, country_01, ...rest } = _values;
    const values = {
      ...rest,
      ['country_01']:
        country_01?.trim()?.toLowerCase() === 'other'
          ? custom_country
          : country_01 || selectedCountry,
      timezone: selectedTimezone,
    };

    const isIndia =
      values?.['country_01']?.trim()?.toLowerCase() === 'india' ||
      selectedCountry?.trim()?.toLowerCase() === 'india';

    if (!isCaptchaVerified) {
      setIsCaptchaVerified(false);
      setSubmitting(false);
      return;
    }

    sendDataToHubspot({
      values,
      portalId,
      formId,
      setFormInfo,
      callback: () => afterSuccessCallback({ email: values?.email }),
      failedCallback: () => afterFailedCallback(),
      isIndia,
    });

    setSubmitting(false);
  };

  const settings = {
    initialValues,
    validationSchema,
    onSubmit,
  };

  // datepicker initialization
  const intializeCalendar = (v) => {
    if (!v || !window.Datepicker) return;
    const today = getTodayDateWithTimezone(v);

    const target = document.getElementById(`datepicker-${uniqueId}`);
    if (target) target.innerHTML = '';

    // Initialize the inline datepicker
    const datepicker = new window.Datepicker(target, {
      format: 'yyyy-mm-dd',
      minDate: today, // Disable past dates
      autohide: false, // Keep it always visible (inline)
    });

    if (datepicker) {
      window.datepicker = datepicker;
      datepicker?.setDate(today); // keeping the current date selected by default
      setFormInfo((prev) => ({
        ...prev,
        selectedDate: today, // getTodayDateWithTimezone(selectedTimezone),
        selectedTimezone: v,
      }));
    }
  };

  const populateTimezone = () => {
    if (typeof Intl.supportedValuesOf === 'function') {
      const timezones = Intl.supportedValuesOf('timeZone');
      const userTimezone = getUserTimezone();

      if (timezones.length > 0) {
        setFormInfo((prev) => ({
          ...prev,
          timezoneOptions: timezones,
          selectedTimezone: selectedTimezone || userTimezone,
        }));
      }
    } else {
      console.error('Intl.supportedValuesOf is not supported in this browser.');
    }
  };

  useEffect(() => {
    if (uniqueId && selectedDate) populateTimezone();
  }, [uniqueId, selectedDate, availableTimeOptions]);

  function handleDate(event) {
    event?.preventDefault();
    event?.stopPropagation();
    const selectedDate = getDateFromTimestamp(event?.detail?.date);

    setFormInfo((prev) => ({
      ...prev,
      selectedDate,
      selectedTime: null,
    }));
  }

  useEffect(() => {
    if (uniqueId && !window.datepicker) {
      const userTimezone = getUserTimezone();
      intializeCalendar(userTimezone);

      document
        .querySelector(`#datepicker-${uniqueId}`)
        ?.addEventListener('changeDate', function (event) {
          handleDate(event);
        });
    }
    return () => {
      document
        .querySelector(`#datepicker-${uniqueId}`)
        ?.removeEventListener('changeDate', function (event) {
          handleDate(event);
        });
      window.datepicker = null;
    };
  }, [uniqueId]);

  const moveToNextAvailableDate = (availableTime = null) => {
    if (!availableTime) return;
    const _selectedDate = getDateFromTimestampBasedOnTimezone(
      availableTime?.startMillisUtc,
      selectedTimezone
    );

    // Set the datepicker to the next date
    window?.datepicker?.setDate(_selectedDate); // nextDate)
    // Update the state/store with the next date
    setFormInfo((prev) => ({
      ...prev,
      selectedDate: _selectedDate,
      selectedTime: null,
    }));
  };

  useEffect(() => {
    if (timeOptions.length > 0 && !fetchingAvailableTime) moveToNextAvailableDate(timeOptions?.[0]);
  }, [fetchingAvailableTime, timeOptions]);

  const disableCalendarDates = () => {
    // disabling the calendar dates before showing the available days to improve the user experience

    const datePicker = document.querySelector('.datepicker.active');
    const hasChildren = datePicker?.childElementCount !== 0;

    if (datePicker && hasChildren) {
      const days = Array.from(
        datePicker?.querySelectorAll('.datepicker-main .datepicker-grid .datepicker-cell.day')
      );
      if (days.length > 0) {
        days.forEach((day) => day?.classList?.add('disabled'));
      }
    }
  };

  const getTimeOptions = async ({ monthOffset = 0 } = {}) => {
    if (fetchingAvailableTime) return;
    disableCalendarDates();

    const isIndia = selectedCountry?.trim()?.toLowerCase() === 'india';

    let options = [];

    setFetchingAvailableTime(true);
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: encodeURIComponent(isIndia ? INDIA_MEETING_LINK_SLUG : MEETING_LINK_SLUG),
        timezone: selectedTimezone?.replaceAll('/', '%2F'),
        monthOffset: monthOffset,
      }),
    };
    try {
      const response = await fetch(MEETING_AVAILIBITY_API, requestOptions);
      const results = await response.json();
      setAPIPayload((prev) => ({
        ...prev,
        monthOffset,
        hasMore: results?.linkAvailability?.hasMore,
      }));
      const durations = results?.linkAvailability?.linkAvailabilityByDuration || {};

      Object.keys(durations).forEach((duration) => {
        options.push(...(durations[duration]?.availabilities || []));
      });
    } catch (error) {
      console.error('Error during API fetch:', error);
      setFetchingAvailableTime(false);
    }

    setFetchingAvailableTime(false);

    const _options = monthOffset === 0 ? options : [...timeOptions, ...options];

    const availableOptions = [..._options]
      ?.map((opt) => {
        if (
          selectedDate ===
          getDateFromTimestampBasedOnTimezone(opt?.startMillisUtc, selectedTimezone)
        )
          return opt;
        return null;
      })
      ?.filter(Boolean);

    setFormInfo((prev) => ({
      ...prev,
      availableTimeOptions: availableOptions,
      timeOptions: _options,
    }));
  };

  useEffect(() => {
    if (!selectedCountry) return;

    // Check if the previous country was India or not
    const prevCountry = prevCountryRef.current;
    const isPrevIndia = prevCountry === 'India';
    const isCurrentIndia = selectedCountry === 'India';

    // Update the ref for the next render
    prevCountryRef.current = selectedCountry;

    // Call getTimeOptions only when switching between India and non-India
    if (isPrevIndia !== isCurrentIndia && !fetchingAvailableTime) {
      getTimeOptions({ monthOffset: 0 });
      window.getTimeOptions = getTimeOptions;
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedTimezone && !fetchingAvailableTime) {
      getTimeOptions({ monthOffset: 0 });
      window.getTimeOptions = getTimeOptions;
    }
  }, [selectedTimezone]);

  useEffect(() => {
    if (APIPayload.monthOffset !== 0 && !fetchingAvailableTime) {
      getTimeOptions({ monthOffset: APIPayload.monthOffset });
      window.getTimeOptions = getTimeOptions;
    }
  }, [APIPayload.monthOffset]);

  // here, filtering the current date time from the available options
  const updateTimeOptionsOnDateSelection = () => {
    const availableOptions = timeOptions
      ?.map((opt) => {
        if (
          selectedDate ===
          getDateFromTimestampBasedOnTimezone(opt?.startMillisUtc, selectedTimezone)
        )
          return opt;
        return null;
      })
      ?.filter(Boolean);

    setFormInfo((prev) => ({
      ...prev,
      availableTimeOptions: availableOptions,
    }));
  };

  useEffect(() => {
    if (selectedDate) updateTimeOptionsOnDateSelection();
  }, [selectedDate, timeOptions, selectedTimezone]);

  // here, showing the only available dates by comparing the calender date and the timestamp getting in the hubspot meeting available api
  const enableDisabledCalenderDate = (_options = []) => {
    const options = _options.length === 0 ? timeOptions : _options;
    const allAvailableDates = options.map((a) =>
      getDateFromTimestampBasedOnTimezone(a?.startMillisUtc, selectedTimezone)
    );

    // Convert to a Set to remove duplicates, then back to an array
    const uniqueDates = [...new Set(allAvailableDates)];

    const uniqueMonths = [...new Set(uniqueDates?.map((a) => a.split('-')[1]))].map((a) =>
      Number(a?.replace('0', ''))
    );

    if (uniqueDates?.length === 0) return;
    const datePicker = document.querySelector(`#datepicker-${uniqueId}`);

    const hasChildren = datePicker?.childElementCount !== 0;

    if (datePicker && hasChildren) {
      const days = Array.from(
        datePicker?.querySelectorAll('.datepicker-main .datepicker-grid .datepicker-cell.day')
      );
      if (days.length > 0) {
        days.forEach((day) => {
          const timestamp = day?.getAttribute('data-date');

          if (uniqueDates?.includes(getDateFromTimestamp(+timestamp))) {
            day?.classList?.remove('disabled');
          } else {
            day?.classList?.add('disabled');
          }
        });
      }

      // enabling / disbaling month
      const controls = datePicker?.querySelector('.datepicker-header .datepicker-controls');
      const selectedMonth = datePicker
        ?.querySelector('.datepicker-header .datepicker-controls .button.view-switch')
        ?.innerHTML?.split(' ')?.[0];
      const selectedMonthNumber = MONTH_MAPPING[selectedMonth?.toLowerCase()];
      const nextMonthNumber = +selectedMonthNumber + 1;

      controls.querySelector('.view-switch').disabled = true;
      if (APIPayload.hasMore || (uniqueMonths.includes(nextMonthNumber) && controls)) {
        controls.querySelector('.next-button').disabled = false;
      } else {
        controls.querySelector('.next-button').disabled = true;
      }

      // adding do-not-enable class if user is on the first month & with this class preventing the dates to be disabled by clicking on the previos button
      controls.querySelector('.prev-button').disabled = +uniqueMonths?.[0] === +selectedMonthNumber;
      if (+uniqueMonths?.[0] === +selectedMonthNumber)
        controls.querySelector('.prev-button').classList.add('do-not-enabled');
      else controls.querySelector('.prev-button').classList.remove('do-not-enabled');
    }
  };

  const onMouseEnter = (e) => {
    const btn = e.target.querySelector('.prev-button.prev-btn');
    if (!btn?.classList?.contains('do-not-enabled')) btn.disabled = false;
  };

  const onMouseLeave = (e) => (e.target.querySelector('.prev-button.prev-btn').disabled = true);

  const delayEnableDisabledCalenderDate = (e) => {
    setTimeout(() => enableDisabledCalenderDate(), 500);
  };

  const getPrevMonthData = () => disableCalendarDates();

  const getNextMonthData = () => {
    disableCalendarDates();

    if (!APIPayload.hasMore) return;
    setAPIPayload((prev) => ({
      ...prev,
      monthOffset: prev.monthOffset + 1,
    }));
  };

  useEffect(() => {
    const timerId = setTimeout(() => {
      enableDisabledCalenderDate(timeOptions);

      if (window.datepicker) {
        const prevBtn = document.querySelector('.datepicker-header .prev-button.prev-btn');
        if (prevBtn) prevBtn.disabled = true;
        document.querySelector('.datepicker-header')?.addEventListener('mouseenter', onMouseEnter);
        document.querySelector('.datepicker-header')?.addEventListener('mouseleave', onMouseLeave);
        document
          .querySelector('.datepicker-header .datepicker-controls button.prev-button')
          ?.addEventListener('click', getPrevMonthData);
        document
          .querySelector('.datepicker-header .datepicker-controls button.next-button')
          ?.addEventListener('click', getNextMonthData);

        Array.from(
          document.querySelectorAll('.datepicker-header .datepicker-controls button')
        ).forEach((a) => a?.addEventListener('click', delayEnableDisabledCalenderDate));
      }
    }, 500);

    return () => {
      if (timerId) clearTimeout(timerId);

      document.querySelector('.datepicker-header')?.removeEventListener('mouseenter', onMouseEnter);
      document.querySelector('.datepicker-header')?.removeEventListener('mouseleave', onMouseLeave);
      document
        .querySelector('.datepicker-header .datepicker-controls button.prev-button')
        ?.removeEventListener('click', getPrevMonthData);
      document
        .querySelector('.datepicker-header .datepicker-controls button.next-button')
        ?.removeEventListener('click', getNextMonthData);

      Array.from(
        document.querySelectorAll('.datepicker-header .datepicker-controls button')
      )?.forEach((a) => a?.removeEventListener('click', delayEnableDisabledCalenderDate));
    };
  }, [selectedDate, timeOptions, uniqueId, selectedTimezone]);

  return (
    <InfoDialogUI
      title={title}
      titleStyle={{
        fontSize: '1.5rem',
        fontWeight: 400,
        textAlign: 'left',
      }}
      show
      onClose={onClose}
      closeIconV2
      size="lg"
      contentClassName="form-container-v2 p-0 w-100 mw-100"
      bodyClassName="w-100"
      dialogClassName="form-design-v2 book-demo-form">
      <div style={{ minHeight: 44, position: 'relative' }} className="form-card">
        <Formik {...settings}>
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleBlur,
            handleSubmit,
            setTouched,
            setValues,
            setErrors,
          }) => (
            <Form
              action=""
              id="myform"
              onSubmit={
                Object.keys(errors)?.length ? () => setTouched({ ...initialTouch }) : handleSubmit
              }>
              {/* <div className="d-flex align-items-center justify-content-center gap-2 w-100"> */}
              <Form.Group className="form-outline inline">
                {errors?.firstname && touched?.firstname ? (
                  <Form.Label className="text-danger">
                    <span>{errors?.firstname}</span>
                  </Form.Label>
                ) : (
                  <Form.Label>
                    <span>First Name *</span>
                  </Form.Label>
                )}
                <Form.Control
                  type="text"
                  maxLength={50}
                  placeholder="First Name"
                  className={cx(errors.firstname && touched.firstname && 'input-error')}
                  name="firstname"
                  onChange={handleChange}
                  onFocus={() => setTouched({ ...touched, firstname: false })}
                  onBlur={handleBlur}
                  value={values.firstname}
                />
              </Form.Group>
              <Form.Group className="form-outline inline">
                {errors?.lastname && touched?.lastname ? (
                  <Form.Label className="text-danger">
                    <span>{errors?.lastname}</span>
                  </Form.Label>
                ) : (
                  <Form.Label>
                    <span>Last Name *</span>
                  </Form.Label>
                )}
                <Form.Control
                  type="text"
                  maxLength={50}
                  placeholder="Last Name"
                  className={cx(errors.lastname && touched.lastname && 'input-error')}
                  name="lastname"
                  onChange={handleChange}
                  onFocus={() => setTouched({ ...touched, lastname: false })}
                  onBlur={handleBlur}
                  value={values.lastname}
                />
              </Form.Group>
              {/* </div> */}
              <Form.Group className="form-outline inline">
                {errors.email && touched.email ? (
                  <Form.Label className="text-danger">
                    <span>{errors.email}</span>
                  </Form.Label>
                ) : (
                  <Form.Label>
                    <span>Work email *</span>
                  </Form.Label>
                )}
                <Form.Control
                  type="email"
                  placeholder="Work email"
                  className={cx(errors.email && touched.email && 'input-error')}
                  name="email"
                  onChange={handleChange}
                  onFocus={() => setTouched({ ...touched, email: false })}
                  onBlur={(e) => handleBlur(e)}
                  value={values.email}
                />
              </Form.Group>
              <Form.Group className="form-outline inline">
                {(errors?.phone && touched?.phone) || contactNumberMessage ? (
                  <Form.Label className="text-danger">
                    <span>{errors?.phone || contactNumberMessage}</span>
                  </Form.Label>
                ) : (
                  <Form.Label>
                    <span>Phone number</span>
                  </Form.Label>
                )}
                <PhoneInput
                  name="phone"
                  placeholder="Phone Number"
                  country={COUNTRY_CODE_MAP[selectedCountry] || 'us'}
                  value={values?.phone}
                  onChange={(contact) => {
                    setValues({ ...values, phone: contact });
                  }}
                  onFocus={() => setTouched({ ...touched, phone: false })}
                  onBlur={() => setTouched({ ...touched, phone: true })}
                  inputClass={'phoneInput'}
                  style={{ width: '100%' }}
                  isValid={(value, data) => {
                    let formatLength = (data?.format?.match(/\./g) || [])?.length;
                    let valueLength = value.length;
                    if (
                      value.replace(data.countryCode, '') !== '' &&
                      valueLength !== formatLength &&
                      valueLength
                    ) {
                      setContactNumberMessage('Please enter valid number');
                      setErrors({
                        ...errors,
                        phone: 'Please enter valid number',
                      });
                    } else {
                      if (errors?.phone) {
                        setErrors({ ...errors, phone: '' });
                        setContactNumberMessage('');
                      }
                    }
                  }}
                  className={`${errors?.phone || contactNumberMessage ? 'errorBorder' : ''}`}
                />
              </Form.Group>
              <Form.Group className="form-outline inline">
                {errors?.company && touched?.company ? (
                  <Form.Label className="text-danger">
                    <span>{errors?.company}</span>
                  </Form.Label>
                ) : (
                  <Form.Label>
                    <span>Company *</span>
                  </Form.Label>
                )}
                <Form.Control
                  type="text"
                  maxLength={50}
                  placeholder="Company"
                  className={cx(errors.company && touched.company && 'input-error')}
                  name="company"
                  onChange={handleChange}
                  onFocus={() => setTouched({ ...touched, company: false })}
                  onBlur={handleBlur}
                  value={values.company}
                />
              </Form.Group>

              <Form.Group className="form-outline inline">
                {errors?.['country_01'] && touched?.['country_01'] ? (
                  <Form.Label className="text-danger">
                    <span>{errors?.['country_01']}</span>
                  </Form.Label>
                ) : (
                  <Form.Label>
                    <span>Country/Region *</span>
                  </Form.Label>
                )}

                <Form.Select
                  className={cx(errors?.['country_01'] && touched?.['country_01'] && 'input-error')}
                  name="country_01"
                  onChange={(e) => {
                    const value = e.target.value;
                    handleChange(e);
                    setValues((prevValues) => ({
                      ...prevValues,
                      ['country_01']: value,
                      custom_country:
                        value?.toLowerCase() === 'other' ? '' : prevValues.custom_country, // Clear custom_country if not Other
                    }));
                    setFormInfo((prev) => ({
                      ...prev,
                      selectedCountry: value,
                    }));
                  }}
                  value={values?.['country_01'] || selectedCountry} // Ensure the select reflects the current value
                >
                  {COUNTRY_LIST.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {values?.['country_01']?.toLowerCase() === 'other' ||
              selectedCountry?.toLowerCase() === 'other' ? (
                <Form.Group className="form-outline">
                  {errors?.custom_country && touched?.custom_country ? (
                    <Form.Label className="text-danger">
                      <span>{errors?.custom_country}</span>
                    </Form.Label>
                  ) : (
                    <Form.Label>
                      <span>Write your country name *</span>
                    </Form.Label>
                  )}
                  <Form.Control
                    type="text"
                    maxLength={50}
                    placeholder="Write your country name"
                    className={cx(errors.custom_country && touched.custom_country && 'input-error')}
                    name="custom_country"
                    onChange={handleChange}
                    onFocus={() => setTouched({ ...touched, custom_country: false })}
                    onBlur={handleBlur}
                    value={values.custom_country}
                  />
                </Form.Group>
              ) : null}

              <Form.Group className="form-outline">
                {errors?.startTime && touched?.startTime ? (
                  <Form.Label className="text-danger">
                    <span>{errors?.startTime}</span>
                  </Form.Label>
                ) : (
                  <Form.Label>
                    <span>Choose a date and time *</span>
                  </Form.Label>
                )}
                <div className="hubspot-form-datepicker">
                  <div id={`datepicker-${uniqueId}`} className="datepicker-form-ui"></div>
                  <div className="time-section" style={{ position: 'relative' }}>
                    {fetchingAvailableTime ? <Loader loading position="absolute" /> : null}

                    <div className="framer-form-input-wrapper framer-form-select-wrapper">
                      <Form.Select
                        className={cx(errors.timezone && touched.timezone && 'input-error', 'mb-3')}
                        name="timezone"
                        onChange={(e) => {
                          const value = e.target.value;
                          handleChange(e);
                          setFormInfo((prev) => ({
                            ...prev,
                            selectedTimezone: value,
                          }));
                          setValues({
                            ...values,
                            timezone: value,
                          });
                          intializeCalendar(value);
                        }}
                        value={values.timezone || selectedTimezone} // Ensure the select reflects the current value
                      >
                        {timezoneOptions.map((t) => (
                          <option value={t}>{t}</option>
                        ))}
                      </Form.Select>
                    </div>

                    {availableTimeOptions?.length > 0 ? (
                      <>
                        <div className="hubspot-form-time-options">
                          {availableTimeOptions
                            ?.map((t, i) => {
                              const start = new Date(t?.startMillisUtc);
                              const startTime = start.toLocaleTimeString('en-US', {
                                timeZone: selectedTimezone,
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                              });

                              return {
                                date: getDateFromTimestampBasedOnTimezone(
                                  t?.startMillisUtc,
                                  selectedTimezone
                                ),
                                startTime,
                                startMillisUtc: t?.startMillisUtc,
                              };
                            })
                            .filter(Boolean)
                            .sort(
                              (a, b) =>
                                new Date(`${a?.date} ${a?.startTime}`).getTime() -
                                new Date(`${b?.date} ${b?.startTime}`).getTime()
                            )
                            .map((t, i) => {
                              return (
                                <span
                                  key={`${i}__${t?.startMillisUtc}`}
                                  className={`btn-time ${
                                    selectedTime === t?.startMillisUtc ? 'selected' : ''
                                  }`}
                                  onClick={(e) => {
                                    e?.stopPropagation();
                                    setFormInfo((prev) => ({
                                      ...prev,
                                      selectedTime: t?.startMillisUtc,
                                    }));
                                    setValues({ ...values, startTime: t?.startMillisUtc });
                                    setTouched({ ...touched, startTime: false });
                                  }}>
                                  {t.startTime}
                                </span>
                              );
                            })
                            .filter(Boolean)}
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
                <Form.Control
                  type="hidden"
                  name="startTime"
                  value={values.startTime || selectedTime}
                />
                <Form.Control
                  type="hidden"
                  name="timezone"
                  value={values.timezone || selectedTimezone}
                />
              </Form.Group>

              <Form.Group className="form-outline">
                {isCaptchaVerified === false ? (
                  <Form.Label className="text-danger">
                    <span>Please fill the captcha</span>
                  </Form.Label>
                ) : null}
                <ReCAPTCHA
                  sitekey={GOOGLE_REAPTCHA_V2_KEY}
                  onChange={(t) => setIsCaptchaVerified(true)}
                />
              </Form.Group>

              <Form.Group className="form-btn">
                <BigButton
                  type={Object.keys(errors)?.length ? 'button' : 'submit'}
                  disabled={isSubmitting}
                  onClick={() => setTouched({ ...initialTouch })}
                  className="bigButton lf-btn-1 w-100"
                  buttonTitle="Talk to an Expert"
                  isLoading={formSubmitting}
                />
              </Form.Group>
            </Form>
          )}
        </Formik>
      </div>
    </InfoDialogUI>
  );
};
